import type { NextRequest } from "next/server";
import { verifyAccessTokenLocally } from "@west-tennessee-consulting/supabase";
import { getServiceSupabase } from "./supabase.js";
import { DebugLogger } from "./debug-logger.js";

// ============================================================================
// STANDARDIZED SUPABASE JWT VERIFICATION UTILITIES
// ============================================================================
//
// PURPOSE: Provide standardized JWT verification using Supabase's built-in authentication
//
// USAGE:
// - verifySupabaseSessionAndGetUser(): For API routes that use cookies/sessions
// - verifySupabaseJwtAndGetUser(): For API routes that receive JWTs in Authorization headers
//
// BENEFITS:
// - Uses Supabase's built-in JWT verification (no manual JWT library needed)
// - Consistent with the rest of the application's authentication system
// - Automatically handles token expiration and validation
// - Fetches user data from app_users table for role-based access control
//
// ============================================================================

// Import unified types
import { AuthenticatedUser } from "./types/auth.js";

/**
 * Standardized Supabase JWT verification using Supabase's built-in authentication.
 * This function uses Supabase's server client to verify the session from cookies
 * and then fetches the user data from the app_users table.
 *
 * @param request - The incoming NextRequest object.
 * @returns A promise that resolves to either the authenticated user data or a NextResponse error object.
 */
export async function verifySupabaseSessionAndGetUser(
  request: NextRequest,
): Promise<AuthenticatedUser | Response> {
  try {
    // Use Supabase server client (uses cookies for session)
    // Built on first call, not at import time (see supabase.ts). The
    // non-null assertion the previous line carried was load-bearing on an
    // eager module const; make the miss explicit instead.
    const supabaseClient = getServiceSupabase();
    if (!supabaseClient) {
      // Our own configuration is missing, which is not the caller's fault and
      // must not read as "your credentials are bad" (USER_FACING_API_ERRORS).
      console.error(
        "[Supabase Auth] service client unavailable - SUPABASE_SERVICE_ROLE_KEY or URL not configured",
      );
      return Response.json({ error: "Authentication is temporarily unavailable" }, { status: 503 });
    }

    // Get the current user from Supabase session
    const {
      data: { user: supabaseUser },
      error: authError,
    } = await supabaseClient.auth.getUser();

    if (authError || !supabaseUser) {
      console.error("Supabase authentication error:", authError);
      
return Response.json(
        { error: "Authentication required" },
        { status: 401 },
      );
    }

    DebugLogger.logAuth("verifySupabaseSessionAndGetUser", {
      supabaseUserId: supabaseUser.id,
      supabaseUserEmail: supabaseUser.email,
      supabaseUserEmailConfirmed: supabaseUser.email_confirmed_at
    }, request);

    // DISABLED: app_users table calls - not using app_users for now
    // const serviceClient = await createServiceRoleClient();
    // let { data: appUser, error: appUserError } = await serviceClient
    //   .from("app_users")
    //   .select(`
    //     id, 
    //     email, 
    //     first_name, 
    //     last_name,
    //     role_id,
    //     roles!inner(
    //       name,
    //       display_name,
    //       permissions
    //     )
    //   `)
    //   .eq("id", supabaseUser.id)
    //   .single();

    DebugLogger.logAuth("verifySupabaseSessionAndGetUser", "App Users Table Disabled - Using Supabase user data directly", request);

    // Return user data directly from Supabase auth without app_users table
    const userEmail = supabaseUser.email || "";
    const userRole = userEmail.includes("@wtc") || userEmail.includes("@westtennessee")
      ? "wtc_employee"
      : "client_user";
    
    return {
      id: supabaseUser.id,
      role: userRole,
      email: supabaseUser.email || "",
      first_name: supabaseUser.user_metadata?.first_name || "",
      last_name: supabaseUser.user_metadata?.last_name || "",
      isAuthenticated: true,
      // Include role information for centralized role checking
      roles: {
        id: '',
        name: userRole,
        display_name: userRole === "wtc_employee" ? "WTC Employee" : "Client User",
        permissions: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as any
    };
  } catch (error) {
    console.error("Supabase session verification error:", error);
    
return Response.json(
      { error: "An unexpected error occurred during authentication" },
      { status: 500 },
    );
  }
}

/**
 * Alternative function that verifies JWT from Authorization header using Supabase's built-in verification.
 * This is useful for API routes that receive JWTs in headers rather than cookies.
 *
 * @param request - The incoming NextRequest object.
 * @returns A promise that resolves to either the authenticated user data or a NextResponse error object.
 */
export async function verifySupabaseJwtAndGetUser(
  request: NextRequest,
): Promise<AuthenticatedUser | Response> {
  const authHeader = request.headers.get("Authorization");
  
  // Handle both "Bearer <token>" and raw token formats
  let token: string | undefined;
  if (authHeader?.startsWith("Bearer ")) {
    token = authHeader.split(" ")[1];
  } else if (authHeader) {
    token = authHeader;
  }

  if (!token) {
    return Response.json(
      { error: "Missing authentication token" },
      { status: 401 },
    );
  }

  try {
    // Extract the Supabase URL from the JWT issuer
    const jwtPayload = JSON.parse(atob(token.split('.')[1]));
    const jwtIssuer = jwtPayload.iss;
    const supabaseUrl = jwtIssuer.replace('/auth/v1', '');
    
    // Create a client specifically for this JWT's Supabase instance
    const { createClient } = await import("@supabase/supabase-js");
    
    // Create service role client for this specific instance
    const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
    if (!serviceRoleKey) {
      throw new Error("SUPABASE_SERVICE_ROLE_KEY environment variable is required");
    }
    
    const serviceClient = createClient(supabaseUrl, serviceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
    });

    // Use Supabase's built-in JWT verification
    const {
      data: { user: supabaseUser },
      error: authError,
    } = await serviceClient.auth.getUser(token);

    if (authError || !supabaseUser) {
      console.error("Supabase JWT verification error:", authError);
      
return Response.json(
        { error: "Invalid or expired token" },
        { status: 401 },
      );
    }

    DebugLogger.logAuth("verifySupabaseJwtAndGetUser", {
      supabaseUserId: supabaseUser.id,
      supabaseUserEmail: supabaseUser.email,
      supabaseUserEmailConfirmed: supabaseUser.email_confirmed_at
    }, request);

    // DISABLED: app_users table calls - not using app_users for now
    // let { data: appUser, error: appUserError } = await serviceClient
    //   .from("app_users")
    //   .select(`
    //     id, 
    //     email, 
    //     first_name, 
    //     last_name,
    //     role_id,
    //     roles!inner(
    //       name,
    //       display_name,
    //       permissions
    //     )
    //   `)
    //   .eq("id", supabaseUser.id)
    //   .single();

    DebugLogger.logAuth("verifySupabaseJwtAndGetUser", "App Users Table Disabled - Using Supabase user data directly", request);

    // Return user data directly from Supabase auth without app_users table
    const userEmail = supabaseUser.email || "";
    const userRole = userEmail.includes("@wtc") || userEmail.includes("@westtennessee")
      ? "wtc_employee"
      : "client_user";
    
    return {
      id: supabaseUser.id,
      role: userRole,
      email: supabaseUser.email || "",
      first_name: supabaseUser.user_metadata?.first_name || "",
      last_name: supabaseUser.user_metadata?.last_name || "",
      isAuthenticated: true,
      // Include role information for centralized role checking
      roles: {
        id: '',
        name: userRole,
        display_name: userRole === "wtc_employee" ? "WTC Employee" : "Client User",
        permissions: {},
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      } as any
    };
  } catch (error) {
    console.error("Supabase JWT verification error:", error);
    
return Response.json(
      { error: "An unexpected error occurred during authentication" },
      { status: 500 },
    );
  }
}

/**
 * Simple function to extract just the email from JWT token
 * This is a lightweight version for cases where you only need the email
 */
export async function extractUserEmailFromJwt(request: NextRequest): Promise<string | null> {
  try {
    DebugLogger.logAuth("extractUserEmailFromJwt", "Function started", request);
    
    const authHeader = request.headers.get("Authorization");
    DebugLogger.logAuth("extractUserEmailFromJwt", {
      authHeaderPresent: !!authHeader,
      authHeaderPreview: authHeader ? authHeader.substring(0, 50) + "..." : "null"
    }, request);
    
    // Handle both "Bearer <token>" and raw token formats
    let token: string | undefined;
    if (authHeader?.startsWith("Bearer ")) {
      token = authHeader.split(" ")[1];
      DebugLogger.logAuth("extractUserEmailFromJwt", "Token extracted from Bearer format", request);
    } else if (authHeader) {
      token = authHeader;
      DebugLogger.logAuth("extractUserEmailFromJwt", "Token extracted as raw token", request);
    }
    
    DebugLogger.logAuth("extractUserEmailFromJwt", {
      tokenExtracted: !!token,
      tokenLength: token ? token.length : 0,
      tokenPreview: token ? token.substring(0, 50) + "..." : "null"
    }, request);
    
    // Check if token looks like a JWT (should have 3 parts separated by dots)
    if (token) {
      const tokenParts = token.split('.');
      DebugLogger.logAuth("extractUserEmailFromJwt", {
        tokenPartsCount: tokenParts.length,
        tokenPartsLengths: tokenParts.map(part => part.length)
      }, request);
      
      // Try to decode the header to see if it's a valid JWT
      try {
        const header = JSON.parse(atob(tokenParts[0]));
        DebugLogger.logAuth("extractUserEmailFromJwt", { jwtHeader: header }, request);
        
        // Try to decode the payload to see the token content
        if (tokenParts.length >= 2) {
          const payload = JSON.parse(atob(tokenParts[1]));
          DebugLogger.logAuth("extractUserEmailFromJwt", {
            jwtPayload: {
              iss: payload.iss,
              aud: payload.aud,
              exp: payload.exp,
              iat: payload.iat,
              sub: payload.sub,
              email: payload.email,
              email_verified: payload.email_verified
            }
          }, request);
          
        }
      } catch (e) {
        // JWT decode failed, continue with verification
      }
    }

    if (!token) {
      DebugLogger.logAuth("extractUserEmailFromJwt", "No token found", request);
      
return null;
    }

    // Extract the Supabase URL from the JWT issuer
    const jwtIssuer = JSON.parse(atob(token.split('.')[1])).iss;
    const supabaseUrl = jwtIssuer.replace('/auth/v1', '');

    // Fast path: verify locally against the token's project JWKS (no GoTrue
    // round-trip). See the SUPABASE_JWT_LOCAL_VERIFICATION rule.
    const local = await verifyAccessTokenLocally(token, { supabaseUrl });
    if (local.status === "verified") {
      DebugLogger.logAuth("extractUserEmailFromJwt", { status: "SUCCESS_LOCAL_JWKS" }, request);
      return local.claims.email;
    }
    if (local.status === "expired") return null;
    // "unavailable"/"failed" → fall through to the network path below.

    // Create a client specifically for this JWT's Supabase instance
    const { createClient } = await import("@supabase/supabase-js");

    // Try with anon key first
    const anonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || process.env.SUPABASE_ANON_KEY;
    
    const regularClient = createClient(supabaseUrl, anonKey!, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
    });
    
    // Also create service role client as backup
    const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
    console.log("Service role key present:", !!serviceRoleKey);
    
    const serviceClient = createClient(supabaseUrl, serviceRoleKey!, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
    });
    console.log("Service role client created successfully");

    // Use Supabase's built-in JWT verification
    console.log("Verifying JWT with Supabase...");
    console.log("About to call auth.getUser with token length:", token.length);
    
    let supabaseUser, authError;
    try {
      // Try with regular client first
      console.log("Trying with regular client...");
      const result = await regularClient.auth.getUser(token);
      supabaseUser = result.data.user;
      authError = result.error;

      DebugLogger.logAuth("extractUserEmailFromJwt", "Regular Client JWT Verification Result", request);
      console.log("Auth error:", authError);
      console.log("Auth error details:", authError ? {
        message: authError.message,
        status: authError.status,
        name: authError.name
      } : "null");
      console.log("Supabase user:", supabaseUser ? {
        id: supabaseUser.id,
        email: supabaseUser.email || "",
        email_confirmed_at: supabaseUser.email_confirmed_at
      } : "null");
      
      // If regular client fails, try service role client
      if (authError) {
        console.log("Regular client failed, trying service role client...");
        const serviceResult = await serviceClient.auth.getUser(token);
        supabaseUser = serviceResult.data.user;
        authError = serviceResult.error;
        
        DebugLogger.logAuth("extractUserEmailFromJwt", "Service Role Client JWT Verification Result", request);
        console.log("Auth error:", authError);
        console.log("Auth error details:", authError ? {
          message: authError.message,
          status: authError.status,
          name: authError.name
        } : "null");
        console.log("Supabase user:", supabaseUser ? {
          id: supabaseUser.id,
          email: supabaseUser.email || "",
          email_confirmed_at: supabaseUser.email_confirmed_at
        } : "null");
      }
    } catch (verificationError) {
      console.error("=== JWT Verification Exception ===");
      console.error("Verification error:", verificationError);
      console.error("Verification error stack:", verificationError instanceof Error ? verificationError.stack : "No stack trace");
      throw verificationError;
    }

    if (authError || !supabaseUser) {
      console.error("Supabase JWT verification error:", authError);
      DebugLogger.logAuth("extractUserEmailFromJwt", "JWT verification failed", request);
      
return null;
    }

    const email = supabaseUser.email || null;
    DebugLogger.logAuth("extractUserEmailFromJwt", {
      status: "SUCCESS",
      extractedEmail: email
    }, request);
    
return email;
  } catch (error) {
    DebugLogger.logError("extractUserEmailFromJwt", error, request);
    
return null;
  }
}

/**
 * Simple function to extract just the user ID from JWT token
 */
export async function extractUserIdFromJwt(request: NextRequest): Promise<string | null> {
  try {
    const authHeader = request.headers.get("Authorization");
    
    // Handle both "Bearer <token>" and raw token formats
    let token: string | undefined;
    if (authHeader?.startsWith("Bearer ")) {
      token = authHeader.split(" ")[1];
    } else if (authHeader) {
      token = authHeader;
    }

    if (!token) {
      return null;
    }

    // Extract the Supabase URL from the JWT issuer
    const jwtPayload = JSON.parse(atob(token.split('.')[1]));
    const jwtIssuer = jwtPayload.iss;
    const supabaseUrl = jwtIssuer.replace('/auth/v1', '');

    // Fast path: verify locally against the token's project JWKS (no GoTrue
    // round-trip). See the SUPABASE_JWT_LOCAL_VERIFICATION rule.
    const local = await verifyAccessTokenLocally(token, { supabaseUrl });
    if (local.status === "verified") return local.claims.sub;
    if (local.status === "expired") return null;
    // "unavailable"/"failed" → fall through to the network path below.

    // Create a client specifically for this JWT's Supabase instance
    const { createClient } = await import("@supabase/supabase-js");

    // Create service role client for this specific instance
    const serviceRoleKey = process.env.SUPABASE_SERVICE_ROLE_KEY;
    if (!serviceRoleKey) {
      return null;
    }

    const serviceClient = createClient(supabaseUrl, serviceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
      },
    });

    // Use Supabase's built-in JWT verification
    const {
      data: { user: supabaseUser },
      error: authError,
    } = await serviceClient.auth.getUser(token);

    if (authError || !supabaseUser) {
      console.error("Supabase JWT verification error:", authError);

return null;
    }

    return supabaseUser.id || null;
  } catch (error) {
    console.error("Error extracting user ID from JWT:", error);
    
return null;
  }
}
